import { useState } from 'react';
import { MapPin, Navigation, Radio, Compass } from 'lucide-react';
import type { VisitDashboard } from '../types';

interface EnhancedVisitMapProps {
  markers: VisitDashboard['markers'];
  routeDistanceKm?: number;
}

export function EnhancedVisitMap({ markers, routeDistanceKm = 0 }: EnhancedVisitMapProps) {
  const [hoveredMarker, setHoveredMarker] = useState<VisitDashboard['markers'][0] | null>(null);

  if (!markers || markers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 mb-3">
          <MapPin className="h-6 w-6" />
        </div>
        <h4 className="text-sm font-bold text-slate-700">No GPS Coordinates Plotted</h4>
        <p className="mt-1 text-xs text-slate-500 max-w-sm">
          Visits planned with latitude/longitude or checked-in stops will be rendered on the live route radar.
        </p>
      </div>
    );
  }

  const lats = markers.map((m) => m.lat);
  const lngs = markers.map((m) => m.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latSpan = Math.max(0.002, maxLat - minLat);
  const lngSpan = Math.max(0.002, maxLng - minLng);

  // Map projection coordinates
  const projectedPoints = markers.map((marker, index) => {
    const x = ((marker.lng - minLng) / lngSpan) * 80 + 10;
    const y = 50 - (((marker.lat - minLat) / latSpan) * 40 + 5);
    return { ...marker, x, y, stopNumber: index + 1 };
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return { fill: '#10B981', ring: 'rgba(16,185,129,0.3)', text: 'text-emerald-700', bg: 'bg-emerald-50' };
      case 'checked_in': return { fill: '#3B82F6', ring: 'rgba(59,130,246,0.3)', text: 'text-blue-700', bg: 'bg-blue-50' };
      case 'cancelled': return { fill: '#EF4444', ring: 'rgba(239,68,68,0.3)', text: 'text-rose-700', bg: 'bg-rose-50' };
      default: return { fill: '#F59E0B', ring: 'rgba(245,158,11,0.3)', text: 'text-amber-700', bg: 'bg-amber-50' };
    }
  };

  // Build SVG path string connecting stops
  const pathD = projectedPoints.reduce((acc, pt, idx) => {
    return idx === 0 ? `M ${pt.x} ${pt.y}` : `${acc} L ${pt.x} ${pt.y}`;
  }, '');

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-sm shadow-emerald-200">
            <Compass className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              GPS Route Radar & Geofence Stops
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-100">
                <Radio className="h-2.5 w-2.5 text-emerald-600 animate-pulse" /> {markers.length} Stops Plotted
              </span>
            </h3>
            <p className="text-[12px] text-slate-500">
              Direct Route Trajectory: <span className="font-semibold text-slate-700">{routeDistanceKm.toFixed(1)} km</span>
            </p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 font-medium text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Completed
          </span>
          <span className="flex items-center gap-1 font-medium text-blue-700">
            <span className="h-2 w-2 rounded-full bg-blue-500" /> Checked In
          </span>
          <span className="flex items-center gap-1 font-medium text-amber-700">
            <span className="h-2 w-2 rounded-full bg-amber-500" /> Planned
          </span>
        </div>
      </div>

      {/* Radar Map Canvas */}
      <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-900 p-2 shadow-inner">
        {/* Decorative Grid Lines */}
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'radial-gradient(#38bdf8 1px, transparent 1px)', backgroundSize: '16px 16px' }} />

        <svg viewBox="0 0 100 60" className="h-64 w-full relative z-10">
          <defs>
            <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#818CF8" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#34D399" stopOpacity="0.8" />
            </linearGradient>
            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          {/* Connected route path line */}
          {projectedPoints.length > 1 && (
            <path
              d={pathD}
              fill="none"
              stroke="url(#routeGradient)"
              strokeWidth="0.8"
              strokeDasharray="2 1.5"
              className="opacity-70 animate-dash"
            />
          )}

          {/* Marker Nodes */}
          {projectedPoints.map((marker) => {
            const style = getStatusColor(marker.status);
            const isHovered = hoveredMarker?.id === marker.id;

            return (
              <g
                key={marker.id}
                onMouseEnter={() => setHoveredMarker(marker)}
                onMouseLeave={() => setHoveredMarker(null)}
                className="cursor-pointer transition-transform duration-200"
              >
                {/* Outer Ring / Pulse */}
                <circle
                  cx={marker.x}
                  cy={marker.y}
                  r={isHovered ? '4.5' : '3.2'}
                  fill={style.ring}
                  className={marker.status === 'checked_in' ? 'animate-ping origin-center' : ''}
                />

                {/* Core Circle */}
                <circle
                  cx={marker.x}
                  cy={marker.y}
                  r={isHovered ? '2.8' : '2.2'}
                  fill={style.fill}
                  stroke="#FFFFFF"
                  strokeWidth="0.6"
                  filter="url(#glow)"
                />

                {/* Stop Number text */}
                <text
                  x={marker.x}
                  y={marker.y + 0.7}
                  fontSize="1.6"
                  fontWeight="bold"
                  fill="#FFFFFF"
                  textAnchor="middle"
                >
                  {marker.stopNumber}
                </text>

                {/* Label text on hover or first 3 */}
                <text
                  x={marker.x}
                  y={marker.y - 3.2}
                  fontSize="2.2"
                  fontWeight="600"
                  fill="#F8FAFC"
                  textAnchor="middle"
                  className="pointer-events-none drop-shadow-md"
                >
                  {marker.label.slice(0, 18)}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Hover Information Banner */}
        {hoveredMarker && (
          <div className="absolute bottom-3 left-3 right-3 z-20 flex items-center justify-between rounded-xl bg-slate-900/90 p-3 text-white backdrop-blur-md border border-slate-700 shadow-xl">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500/20 text-blue-400">
                <Navigation className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-bold text-white">{hoveredMarker.label}</p>
                <p className="text-[10px] text-slate-400 font-mono">
                  {hoveredMarker.lat.toFixed(5)}, {hoveredMarker.lng.toFixed(5)}
                </p>
              </div>
            </div>
            <span className="rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
              {hoveredMarker.status.replace('_', ' ')}
            </span>
          </div>
        )}
      </div>

      {/* Plotted Stops List Row */}
      <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1">
        {projectedPoints.map((pt) => {
          const color = getStatusColor(pt.status);
          return (
            <div
              key={pt.id}
              className={`flex items-center gap-1.5 shrink-0 rounded-xl px-2.5 py-1 text-xs border border-slate-200/80 ${color.bg}`}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-900 text-[9px] font-bold text-white">
                {pt.stopNumber}
              </span>
              <span className={`font-semibold truncate max-w-[120px] ${color.text}`}>
                {pt.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
